import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/api/client";
import { Radar, LogIn } from "lucide-react";
import { LOGIN } from "@/constants/testIds";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      const from = location.state?.from?.pathname;
      if (from) navigate(from, { replace: true });
      else if (u.role === "engineer") navigate("/engineer", { replace: true });
      else if (u.role === "customer") navigate("/portal", { replace: true });
      else navigate("/app/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5">
      {/* LEFT — clean branded panel with logo as background */}
      <div className="relative hidden overflow-hidden lg:col-span-3 lg:flex bg-primary text-primary-foreground">
        {/* Huge logo watermark */}
        <img
          src="https://customer-assets-agu9un31.emergentagent.net/job_field-call-mgmt/artifacts/fy12dps4_ChatGPT%20Image%20Jul%2028%2C%202026%2C%2011_36_08%20PM.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full object-contain opacity-[0.14]"
        />
        <div className="relative z-10 flex w-full flex-col justify-between p-12">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] leading-none opacity-90">
            Field Call Management
          </p>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] opacity-70">© HCIT Services · Smarter approach to IT</p>
        </div>
      </div>

      {/* RIGHT — form */}
      <div className="flex items-center justify-center bg-background p-6 sm:p-12 lg:col-span-2">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <p className="label-eyebrow text-primary">Sign in</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight">Welcome HCIT.</h2>
            <p className="mt-2 text-sm text-muted-foreground">Access Your CRM Console</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-eyebrow mb-1.5 block">Email</label>
              <input
                data-testid={LOGIN.emailInput}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm outline-none ring-offset-background transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Password</label>
              <input
                data-testid={LOGIN.passwordInput}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm outline-none ring-offset-background transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div data-testid="login-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {err}
              </div>
            )}

            <button
              data-testid={LOGIN.submitButton}
              type="submit"
              disabled={busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
            >
              <LogIn className="h-4 w-4" />
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
